import React, { useState, useEffect, useRef } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import { i18n } from "../../translate/i18n";

import { Stack } from "@mui/material";
import {
  AlertTriangle,
  Bot,
  ListPlus,
  MessageSquareText,
  Plus,
  TimerReset,
  Trash2,
} from "lucide-react";
import ChatiaDialogActions from "../ChatiaDialogActions";
import { CloseXIcon } from "../ChatiaIcons";

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark" || theme.palette.type === "dark";

  return {
    root: {
      display: "flex",
      flexWrap: "wrap",
    },
    dialogPaper: {
      ...(theme.chatiaDS?.dialogPaper || {}),
      "& .MuiDialogContent-root": {
        flex: 1,
        overflowY: "auto !important",
        overflowX: "hidden",
        background: isDark ? "#121212" : "#ffffff",
        ...theme.scrollbarStyles,
      },
      "& .MuiDialogActions-root": {
        flexShrink: 0,
        padding: "12px 16px",
        background: isDark ? "#121212" : "#ffffff",
      },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark ? `${theme.palette.primary.main} !important` : undefined,
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark ? `${theme.palette.primary.main} !important` : undefined,
      },
    },
    header: {
      ...(theme.chatiaDS?.header || {}),
      flexShrink: 0,
    },
    headerTitle: {
      ...(theme.chatiaDS?.headerTitle || {}),
    },
    closeButton: {
      backgroundColor: theme.chatiaDS?.closeButton?.backgroundColor || "rgba(255,255,255,0.08)",
      color: theme.chatiaDS?.closeButton?.color || "#ffffff",
      transition: theme.chatiaDS?.closeButton?.transition || "all 0.2s ease",
      "&:hover": {
        backgroundColor: theme.chatiaDS?.closeButtonHover?.backgroundColor || "rgba(255,255,255,0.18)",
        color: "#ffffff",
      },
    },
    content: {
      padding: "18px 20px !important",
      maxHeight: "70vh",
    },
    bodyCard: {
      gap: 12,
      display: "flex",
      flexDirection: "column",
    },
    infoCard: {
      background: theme.mode === "dark" ? "#16191f" : "#ffffff",
      color: theme.mode === "dark" ? "#e9edef" : "#111b21",
      border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
      borderRadius: 12,
      padding: 16,
      boxShadow: "0 10px 28px rgba(0, 0, 0, 0.16)",
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 14,
      color: theme.mode === "dark" ? "#e9edef" : "#111b21",
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: 0.1,
    },
    cardIcon: {
      width: 32,
      height: 32,
      borderRadius: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.04)",
      border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
      color: theme.palette.primary.main,
      "& svg": {
        color: theme.palette.primary.main,
        stroke: theme.palette.primary.main,
      },
    },
    addButton: {
      borderRadius: "10px !important",
      minWidth: 44,
      height: 38,
      boxShadow: "none !important",
      color: theme.mode === "dark" ? "#e9edef !important" : "#111b21 !important",
      background: theme.mode === "dark" ? "#1d222b !important" : "#f7f8fa !important",
      border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.10)" : "1px solid rgba(15, 23, 42, 0.10)",
      "&:hover": {
        background: theme.mode === "dark" ? "#242a35 !important" : "#eef1f4 !important",
        borderColor: theme.mode === "dark" ? "rgba(255, 255, 255, 0.16)" : "rgba(15, 23, 42, 0.16)",
        boxShadow: "none !important",
      },
    },
    optionLabel: {
      fontSize: 12,
      fontWeight: 700,
      color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },
    deleteButton: {
      color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
      padding: 7,
      borderRadius: 8,
      "&:hover": {
        color: "#ffffff",
        background: "rgba(255,255,255,0.08)",
      },
    },
  };
});

const FlowBuilderMenuModal = ({ open, onSave, onUpdate, data, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);

  const [rule, setRule] = useState();

  const [textDig, setTextDig] = useState();

  const [arrayOption, setArrayOption] = useState([]);

  const [timeoutSeconds, setTimeoutSeconds] = useState(0);
  const [aiTimeoutSeconds, setAiTimeoutSeconds] = useState(0);

  const [invalidOptionMessage, setInvalidOptionMessage] = useState("");

  const [labels, setLabels] = useState({
    title: i18n.t("flowBuilderModals.menuModal.titleAdd"),
    btn: i18n.t("flowBuilderModals.menuModal.buttonAdd")
  });

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: i18n.t("flowBuilderModals.menuModal.titleEdit"),
        btn: i18n.t("flowBuilderModals.menuModal.buttonSave")
      });
      setTextDig(data.data.message);
      setArrayOption(data.data.arrayOption);
      setTimeoutSeconds(data.data.timeoutSeconds || 0);
      setAiTimeoutSeconds(data.data.aiTimeoutSeconds || 0);
      setInvalidOptionMessage(data.data.invalidOptionMessage || "");
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({
        title: i18n.t("flowBuilderModals.menuModal.titleAdd"),
        btn: i18n.t("flowBuilderModals.menuModal.buttonAdd")
      });
      setTextDig();
      setArrayOption([]);
      setTimeoutSeconds(0);
      setAiTimeoutSeconds(0);
      setInvalidOptionMessage("");
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSaveContact = async () => {
    if (open === "edit") {
      handleClose();
      onUpdate({
        ...data,
        data: { message: textDig, arrayOption: arrayOption, timeoutSeconds: parseInt(timeoutSeconds) || 0, aiTimeoutSeconds: parseInt(aiTimeoutSeconds) || 0, invalidOptionMessage: invalidOptionMessage }
      });
      return;
    } else if (open === "create") {
      handleClose();
      onSave({
        message: textDig,
        arrayOption: arrayOption,
        timeoutSeconds: parseInt(timeoutSeconds) || 0,
        aiTimeoutSeconds: parseInt(aiTimeoutSeconds) || 0,
        invalidOptionMessage: invalidOptionMessage
      });
    }
  };

  const removeOption = number => {
    setArrayOption(old => old.filter(item => item.number !== number));
  };

  const CardTitle = ({ icon: Icon, children }) => (
    <div className={classes.cardHeader}>
      <span className={classes.cardIcon}>
        <Icon size={16} />
      </span>
      <span>{children}</span>
    </div>
  );

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <div className={classes.header}>
          <Typography className={classes.headerTitle}>{labels.title}</Typography>
          <IconButton size="small" onClick={handleClose} className={classes.closeButton}>
            <CloseXIcon style={{ fontSize: 18 }} />
          </IconButton>
        </div>
        <Stack>
          <DialogContent className={classes.content}>
            <Stack className={classes.bodyCard}>
              <Stack className={classes.infoCard}>
                <CardTitle icon={MessageSquareText}>
                  {i18n.t("flowBuilderModals.menuModal.fields.explanationMessage")}
                </CardTitle>
                <TextField
                  label={i18n.t("flowBuilderModals.menuModal.fields.explanationMessage")}
                  minRows={4}
                  name="text"
                  multiline
                  variant="outlined"
                  value={textDig}
                  onChange={e => setTextDig(e.target.value)}
                  style={{ width: "100%" }}
                />
              </Stack>

              <Stack className={classes.infoCard}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" style={{ marginBottom: 14 }}>
                  <CardTitle icon={ListPlus}>
                    {i18n.t("flowBuilderModals.menuModal.fields.addOption")}
                  </CardTitle>
                  <Button
                    onClick={() =>
                      setArrayOption(old => [
                        ...old,
                        { number: old.length + 1, value: "" }
                      ])
                    }
                    className={classes.addButton}
                    variant="contained"
                  >
                    <Plus size={17} />
                  </Button>
                </Stack>
                <Stack style={{ gap: 10 }}>
                  {arrayOption.map((item, index) => (
                    <Stack width={"100%"} key={item.number} style={{ gap: 6 }}>
                      <Typography className={classes.optionLabel}>
                        {i18n.t("flowBuilderModals.menuModal.fields.typeOption", { number: item.number })}
                      </Typography>
                      <Stack direction={"row"} width={"100%"} style={{ gap: "8px" }}>
                        <TextField
                          placeholder={i18n.t("flowBuilderModals.menuModal.fields.optionPlaceholder")}
                          variant="outlined"
                          defaultValue={item.value}
                          style={{ width: "100%" }}
                          onChange={event =>
                            setArrayOption(old => {
                              const newArr = [...old];
                              newArr[index].value = event.target.value;
                              return newArr;
                            })
                          }
                        />
                        {arrayOption.length === item.number && (
                          <IconButton className={classes.deleteButton} onClick={() => removeOption(item.number)}>
                            <Trash2 size={17} />
                          </IconButton>
                        )}
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Stack>

              <Stack className={classes.infoCard}>
                <CardTitle icon={AlertTriangle}>
                  {i18n.t("flowBuilderModals.menuModal.invalidOption.title")}
                </CardTitle>
                <TextField
                  label={i18n.t("flowBuilderModals.menuModal.invalidOption.messageLabel")}
                  minRows={2}
                  multiline
                  variant="outlined"
                  value={invalidOptionMessage}
                  onChange={e => setInvalidOptionMessage(e.target.value)}
                  style={{ width: "100%" }}
                  placeholder={i18n.t("flowBuilderModals.menuModal.invalidOption.placeholder")}
                  helperText={i18n.t("flowBuilderModals.menuModal.invalidOption.helperText")}
                />
              </Stack>

              <Stack className={classes.infoCard}>
                <CardTitle icon={TimerReset}>
                  {i18n.t("flowBuilderModals.menuModal.timeout.title")}
                </CardTitle>
                <TextField
                  label={i18n.t("flowBuilderModals.menuModal.timeout.label")}
                  type="number"
                  variant="outlined"
                  value={timeoutSeconds}
                  onChange={e => setTimeoutSeconds(e.target.value)}
                  style={{ width: "100%" }}
                  InputProps={{ inputProps: { min: 0 } }}
                  helperText={timeoutSeconds > 0 ? i18n.t("flowBuilderModals.menuModal.timeout.helperText", { seconds: timeoutSeconds }) : ""}
                />
              </Stack>

              <Stack className={classes.infoCard}>
                <CardTitle icon={Bot}>
                  {i18n.t("flowBuilderModals.menuModal.aiTimeout.title")}
                </CardTitle>
                <TextField
                  label={i18n.t("flowBuilderModals.menuModal.aiTimeout.label")}
                  type="number"
                  variant="outlined"
                  value={aiTimeoutSeconds}
                  onChange={e => setAiTimeoutSeconds(e.target.value)}
                  style={{ width: "100%" }}
                  InputProps={{ inputProps: { min: 0 } }}
                  helperText={aiTimeoutSeconds > 0 ? i18n.t("flowBuilderModals.menuModal.aiTimeout.helperText", { seconds: aiTimeoutSeconds }) : ""}
                />
              </Stack>
            </Stack>
          </DialogContent>
          <ChatiaDialogActions
            type="button"
            onClick={() => handleSaveContact()}
            saveText={`${labels.btn}`}
          />
        </Stack>
      </Dialog>
    </div>
  );
};

export default FlowBuilderMenuModal;
